import {Injectable} from "@angular/core";
import {BehaviorSubject, distinctUntilChanged, filter, Observable, switchMap} from "rxjs";
import {ProjectsStatusEnum} from "@shared/enums/project-statuses.enum";
import {
    ProjectProfilesFundsInterface,
    ProjectsInterface,
    ProjectsTradeDataCountInterface,
    ProjectsTradeDataInterface
} from "@shared/interfaces/projects.interface";
import {TenantApiService} from "@core/services/tenant.api.service";
import {ProjectActiveProfilesInterface} from "@shared/interfaces/project-active-profiles.interface";

@Injectable()
export class TenantService {

    tenantId$: Observable<number | null>;
    private tenantIdSubject$: BehaviorSubject<number | null>;

    constructor(
        private readonly tenantApiService: TenantApiService,
    ) {
    }

    init(): void {
        this.tenantIdSubject$ = new BehaviorSubject<number | null>(null);
        this.tenantId$ = this.getTenantId();
    }

    setTenantId(id: number): void {
        this.tenantIdSubject$.next(id);
    }

    getTenantId(): Observable<number | null> {
        return this.tenantIdSubject$.asObservable().pipe(
            distinctUntilChanged(),
            filter(tenant => !!tenant),
        );
    }

    getAllProjects(filter?: ProjectsStatusEnum[]): Observable<ProjectsInterface[]> {
        return this.tenantId$.pipe(
            switchMap(tenantId => this.tenantApiService.getAllProjects(tenantId!, filter))
        );
    }

    getAllProjectsListInfo() {
        return this.tenantId$.pipe(
            switchMap(tenantId => this.tenantApiService.getAllProjectsListInfo(tenantId!))
        );
    }

    getProjectsTradeDataCount(profileIds: number[], projectId: number, dates: string[]): Observable<ProjectsTradeDataCountInterface[]> {
        return this.tenantId$.pipe(
            switchMap(tenantId => this.tenantApiService.getProjectsTradesDataCount(tenantId!, profileIds, projectId, dates))
        );
    }

    getProjectsTradeData(dates: string[], filters: any, projectId: number, profilesId: number[]): Observable<ProjectsTradeDataInterface> {
        return this.tenantId$.pipe(
            switchMap(tenantId => this.tenantApiService.getProjectsTradesData(tenantId!, dates, projectId, profilesId, filters))
        );
    }

    createProject(projectName: string): Observable<ProjectsInterface> {
        return this.tenantId$.pipe(
            switchMap(tenantId => this.tenantApiService.createProject(tenantId!, projectName))
        );
    }

    updateProject( projectId: number, projectName: string): Observable<ProjectsInterface> {
        return this.tenantId$.pipe(
            switchMap(tenantId => this.tenantApiService.updateProject(tenantId!, projectId, projectName))
        );
    }

    updateProjectStatus(projectId: number, status: ProjectsStatusEnum): Observable<void> {
        return this.tenantId$.pipe(
            switchMap(tenantId => this.tenantApiService.updateProjectStatus(tenantId!, projectId, status))
        );
    }

    getProjectActiveProfiles(projectId: number): Observable<ProjectActiveProfilesInterface[]> {
        return this.tenantId$.pipe(
            switchMap(tenantId => this.tenantApiService.getProjectActiveProfiles(tenantId!, projectId))
        );
    }

    deleteProject(projectId: number) {
        return this.tenantId$.pipe(
            switchMap(tenantId => this.tenantApiService.deleteProject(tenantId!, projectId))
        );
    }

    showProjectFunds(projectId: number): Observable<ProjectProfilesFundsInterface> {
        return this.tenantId$.pipe(
            switchMap(tenantId => this.tenantApiService.showProjectFunds(tenantId, projectId)),
        );
    }


}
